import { useTranslation } from '@ufc/core'
import { cn, Button } from '@ufc/uikit'
import type { WeighbridgeEvent } from '../../domain/types'

interface SessionHistoryPanelProps {
  events: WeighbridgeEvent[]
  onClear: () => void
  className?: string
}

const EVENT_COLORS: Partial<Record<WeighbridgeEvent['type'], string>> = {
  weight_captured: 'text-green-600 dark:text-green-400',
  capture_error: 'text-red-600 dark:text-red-400',
  gate_changed: 'text-blue-600 dark:text-blue-400',
  anpr_detected: 'text-purple-600 dark:text-purple-400',
  connected: 'text-green-500 dark:text-green-400',
  disconnected: 'text-red-500 dark:text-red-400',
  internet_connected: 'text-green-500 dark:text-green-400',
  internet_disconnected: 'text-amber-600 dark:text-amber-400',
}

/** Icon prefix per audit eventStatus (boot_log events only) */
const BOOT_STATUS_ICONS: Record<string, string> = {
  success: '✅',
  failure: '❌',
  info: 'ℹ️',
  ignored: '⏭️',
}

/** Colour per audit eventStatus (boot_log events only) */
const BOOT_STATUS_COLORS: Record<string, string> = {
  success: 'text-green-600 dark:text-green-400',
  failure: 'text-red-600 dark:text-red-400',
  info: 'text-blue-600 dark:text-blue-400',
  ignored: 'text-muted-foreground',
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function formatStageLabel(stage: WeighbridgeEvent['stage'], t: ReturnType<typeof useTranslation>['t']): string {
  if (!stage) return ''
  return stage === 'first_weight'
    ? t('weighbridgeOps.stage.firstWeight')
    : t('weighbridgeOps.stage.secondWeight')
}

function formatGateLabel(gateSide: WeighbridgeEvent['gateSide'], t: ReturnType<typeof useTranslation>['t']): string {
  if (!gateSide) return ''
  return gateSide === 'entry'
    ? t('weighbridgeOps.gate.entry')
    : t('weighbridgeOps.gate.exit')
}

function formatSourceLabel(source: WeighbridgeEvent['source'], t: ReturnType<typeof useTranslation>['t']): string {
  if (!source) return ''
  if (source === 'qr_scan') return t('weighbridgeOps.history.sourceQrScan')
  if (source === 'anpr') return t('weighbridgeOps.history.sourceAnpr')
  return t('weighbridgeOps.history.sourceManual')
}

function getEventIdentifier(event: WeighbridgeEvent): string {
  if (event.direction === 'inbound') {
    return event.shipmentNumber ?? event.orderNumber ?? ''
  }
  return event.orderNumber ?? ''
}

function getFlowLabel(event: WeighbridgeEvent, t: ReturnType<typeof useTranslation>['t']): string {
  if (event.direction === 'inbound') {
    return t('weighbridgeOps.queue.shipmentNumber')
  }
  return t('weighbridgeOps.queue.orderNumber')
}

function formatEventMessage(event: WeighbridgeEvent, t: ReturnType<typeof useTranslation>['t']): string {
  const stageLabel = formatStageLabel(event.stage, t)
  const identifier = getEventIdentifier(event)
  const flowLabel = getFlowLabel(event, t)
  const orderSuffix = identifier ? ` (${flowLabel}: ${identifier})` : ''
  const stageSuffix = stageLabel ? ` (${stageLabel})` : ''

  switch (event.type) {
    case 'connected':
      return t('weighbridgeOps.history.connected', { defaultValue: 'Connected to weighbridge controller' })
    case 'disconnected':
      return t('weighbridgeOps.history.disconnected', { defaultValue: 'Disconnected from controller' })
    case 'order_queued':
      return t('weighbridgeOps.history.orderQueued', {
        orderNumber: identifier,
        identifier,
        flowLabel,
        source: formatSourceLabel(event.source, t),
        stage: stageLabel,
        defaultValue: `${flowLabel} ${identifier} queued via ${event.source ?? ''} — ${stageLabel}`,
      })
    case 'order_enqueued':
      return t('weighbridgeOps.history.orderEnqueued', {
        orderNumber: identifier,
        identifier,
        flowLabel,
        stage: stageLabel,
        defaultValue: `${flowLabel} ${identifier} waiting in queue — ${stageLabel}`,
      })
    case 'capture_started':
      return t('weighbridgeOps.history.captureStarted', {
        stage: stageLabel,
        orderSuffix,
        defaultValue: `${stageLabel} capture started${orderSuffix}`,
      })
    case 'weight_captured':
      return t('weighbridgeOps.history.weightCaptured', {
        stage: stageLabel,
        weight: event.weight?.toLocaleString() ?? '',
        orderSuffix,
        defaultValue: `${stageLabel} captured: ${event.weight?.toLocaleString() ?? ''} kg${orderSuffix}`,
      })
    case 'weight_saved':
      return t('weighbridgeOps.history.weightSaved', {
        stage: stageLabel,
        weight: event.weight?.toLocaleString() ?? '',
        orderSuffix,
        defaultValue: `${stageLabel} saved: ${event.weight?.toLocaleString() ?? ''} kg${orderSuffix}`,
      })
    case 'capture_error': {
      const errorDetail = event.errorDetail ?? ''
      if (errorDetail) {
        // Translate errorDetail as an i18n key (Strapi now sends i18n keys, not English strings)
        // Pass errorParams for interpolation (e.g. bagsWeightVarianceExceeded).
        // flowLabel/identifier let direction-aware keys (e.g. weightSubmissionFailed)
        // say "order"/"shipment" correctly instead of a hardcoded word.
        const translatedError = t(errorDetail, {
          flowLabel,
          identifier,
          ...event.errorParams,
          defaultValue: errorDetail,
        })
        return t('weighbridgeOps.history.captureError', {
          stage: stageLabel,
          error: translatedError,
          orderSuffix,
          defaultValue: `${stageLabel} error: ${translatedError}${orderSuffix}`,
        })
      }
      // Synthetic error — message is itself an i18n key
      return t(event.message, {
        stage: stageLabel,
        orderSuffix,
        defaultValue: event.message,
      })
    }
    case 'gate_changed': {
      if (event.gateStatus) {
        // Successful gate event
        return t('weighbridgeOps.history.gateChanged', {
          gate: formatGateLabel(event.gateSide, t),
          status: event.gateStatus === 'open' ? t('weighbridgeOps.gate.open', { defaultValue: 'open' }) : t('weighbridgeOps.gate.closed', { defaultValue: 'closed' }),
          stageSuffix,
          defaultValue: `${formatGateLabel(event.gateSide, t)} gate ${event.gateStatus}${stageSuffix}`,
        })
      }
      // Failure gate event — message is an i18n key (gateOpenFailed, gateCloseFailed, etc.)
      // Translate errorDetail as an i18n key before using as interpolation
      const translatedGateError = event.errorDetail
        ? t(event.errorDetail, { ...event.errorParams, defaultValue: event.errorDetail })
        : ''
      return t(event.message, {
        error: translatedGateError,
        stage: stageLabel,
        defaultValue: translatedGateError ? `${event.message}: ${translatedGateError}` : event.message,
      })
    }
    case 'anpr_detected':
      return t('weighbridgeOps.history.anprDetected', {
        plate: event.plate ?? '',
        orderSuffix,
        defaultValue: `ANPR: ${event.plate ?? ''}${orderSuffix}`,
      })
    case 'scale_not_clear':
      return t('weighbridgeOps.history.scaleNotClear', {
        weight: event.weight?.toLocaleString() ?? '',
        attempt: event.attempt ?? '',
        maxAttempts: event.maxAttempts ?? '',
        orderSuffix,
        defaultValue: `Scale not clear (${event.weight?.toLocaleString() ?? ''} kg) — attempt ${event.attempt ?? ''}/${event.maxAttempts ?? ''}${orderSuffix}`,
      })
    case 'scale_cleared':
      return t('weighbridgeOps.history.scaleCleared', {
        stage: stageLabel,
        orderSuffix,
        defaultValue: `Scale cleared — ${stageLabel} ready${orderSuffix}`,
      })
    case 'truck_departed':
      return t('weighbridgeOps.history.truckDeparted', {
        stage: stageLabel,
        orderSuffix,
        defaultValue: `Truck departed — ${stageLabel} exit gate closed${orderSuffix}`,
      })
    case 'stage_locked':
      return t('weighbridgeOps.history.stageLocked', {
        stage: stageLabel,
        orderNumber: event.lockedByOrder ?? identifier,
        identifier: event.lockedByOrder ?? identifier,
        flowLabel,
        defaultValue: `⚠️ ${stageLabel} LOCKED — error for ${flowLabel} ${event.lockedByOrder ?? identifier}. Open exit gate to unlock.`,
      })
    case 'stage_unlocked':
      return t('weighbridgeOps.history.stageUnlocked', {
        stage: stageLabel,
        defaultValue: `✅ ${stageLabel} unlocked — resuming normal operation.`,
      })
    case 'boot_log': {
      // event.message is the raw backend eventType (e.g. "gate_open_command_issued").
      // Try an explicit boot i18n key first, then fall back to a prettified snake_case label.
      const rawType = event.message ?? '?'
      const prettyDefault = rawType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      const label = t(`weighbridgeOps.history.boot.${rawType}`, { stage: stageLabel, defaultValue: prettyDefault })

      // errorDetail may itself be an i18n key (Strapi error keys) or a plain string.
      const translatedError = event.errorDetail
        ? t(event.errorDetail, { ...event.errorParams, defaultValue: event.errorDetail })
        : ''

      const icon = BOOT_STATUS_ICONS[event.eventStatus ?? ''] ?? '📋'
      const fullMsg = translatedError ? `${label}: ${translatedError}` : label
      return `${icon} ${fullMsg}`
    }
    default:
      return t(event.message, { defaultValue: event.message })
  }
}

export function SessionHistoryPanel({ events, onClear, className }: SessionHistoryPanelProps) {
  const { t } = useTranslation()

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold">{t('weighbridgeOps.history.title')}</h4>
          <p className="text-xs text-muted-foreground">{t('weighbridgeOps.history.sinceNote')}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClear} disabled={events.length === 0}>
          {t('weighbridgeOps.history.clearButton')}
        </Button>
      </div>

      <div className="max-h-48 overflow-y-auto rounded-lg border border-border bg-muted/30 p-2">
        {events.length === 0 ? (
          <p className="p-2 text-center text-xs text-muted-foreground">
            {t('weighbridgeOps.history.empty')}
          </p>
        ) : (
          <ul className="space-y-0.5">
            {events.slice(0, 50).map((event) => (
              <li key={event.id} className="flex items-start gap-3 px-2 py-1 text-xs">
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {formatTime(event.timestamp)}
                </span>
                <span className={cn(
                  'flex-1',
                  event.type === 'boot_log'
                    ? (BOOT_STATUS_COLORS[event.eventStatus ?? ''] ?? 'text-amber-600 dark:text-amber-400')
                    : (EVENT_COLORS[event.type] ?? 'text-foreground'),
                )}>
                  {formatEventMessage(event, t)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
